import { useCallback, useState } from 'react';
import { Upload } from 'react-feather';

import { BalancedMainHeading } from '~ui/app/headings/balanced-main-heading';
import { Button } from '~ui/base/button';
import { Typography } from '~ui/base/typography';

import { DatasetsEmpty } from '~modules/datasets/components/datasets-empty';
import { DatasetsError } from '~modules/datasets/components/datasets-error';
import { DatasetsSkeleton } from '~modules/datasets/components/datasets-skeleton';
import { DatasetsTable } from '~modules/datasets/components/datasets-table';
import { TemporaryLinkDialog } from '~modules/datasets/components/temporary-link-dialog';
import { UploadDialog } from '~modules/datasets/components/upload-dialog';
import { useDatasets } from '~modules/datasets/hooks/use-datasets';
import { EpflSection } from '~modules/epfl/components/epfl-section';
import type { Dataset } from '~lib/storage/datasets';

function DatasetsPage() {
  const { status, datasets, error, refresh } = useDatasets();
  const [uploadOpen, setUploadOpen] = useState(false);
  // Dataset whose zip the temporary-link dialog is open for.
  const [linkTarget, setLinkTarget] = useState<Dataset | null>(null);
  const closeLinkDialog = useCallback(() => setLinkTarget(null), []);

  const existingNames = datasets.map((d) => d.name);

  return (
    <div className="flex flex-col gap-y-8">
      <div className="flex items-start justify-between gap-x-6">
        <div className="flex flex-col gap-y-3">
          <BalancedMainHeading>Datasets</BalancedMainHeading>
          <Typography
            variant="body-lg-extralight"
            as="p"
            className="max-w-2xl text-muted-foreground"
          >
            Shared datasets for local development and CI. Expand a row to
            download individual files; the row icons grab the whole dataset —
            as a folder of loose files, or as a pre-packaged{' '}
            <span className="font-mono">.zip</span> when one&apos;s been
            uploaded. The link icon mints a temporary URL for that{' '}
            <span className="font-mono">.zip</span> for jobs that can&apos;t
            sign in. Use <span className="font-medium text-foreground">Upload
            dataset</span> to stage new files.
          </Typography>
        </div>
        <Button
          size="md"
          onClick={() => setUploadOpen(true)}
          className="mt-1 shrink-0"
        >
          <Upload size={16} aria-hidden />
          Upload dataset
        </Button>
      </div>

      {(status === 'idle' || status === 'loading') && <DatasetsSkeleton />}
      {status === 'error' && error && (
        <DatasetsError error={error} onRetry={refresh} />
      )}
      {status === 'success' && datasets.length === 0 && <DatasetsEmpty />}
      {status === 'success' && datasets.length > 0 && (
        <DatasetsTable datasets={datasets} onCreateLink={setLinkTarget} />
      )}

      <hr className="mt-4 border-border/60" />
      <EpflSection />

      {uploadOpen && (
        <UploadDialog
          existingNames={existingNames}
          onClose={() => setUploadOpen(false)}
          onUploaded={refresh}
        />
      )}
      {linkTarget?.zipFile && (
        <TemporaryLinkDialog
          datasetName={linkTarget.name}
          zipFile={linkTarget.zipFile}
          onClose={closeLinkDialog}
        />
      )}
    </div>
  );
}

export { DatasetsPage };
